import { clamp, omit, orderBy, toNumber } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Row, Spacer } from "../../../../components/Layout/Layout";
import {
  AlarmHandlingDetailsParams,
  Calendar,
  RespondentGroupResponse,
} from "../../../../core/api/responsecenters/types";
import useTranslations from "../../../../core/i18n/useTranslations";
import Button from "../../../../ui-lib/components/Button/Button";
import NumberInput from "../../../../ui-lib/components/Inputs/NumberInput";
import Table, { TableColumn } from "../../../../ui-lib/components/Tables/Table";
import TableCell from "../../../../ui-lib/components/Tables/TableCell";
import { RepeatControl } from "./Controls/RepeatControl";
import { RespondentGroupControl } from "./Controls/RespondentGroupControl";
import { encodeTime, TimeControl } from "./Controls/TimeControl";
import { TimeoutControl } from "./Controls/TimeoutControl";
import { TypeControl } from "./Controls/TypeControl";
import NoItems from "../../../../ui-lib/components/Tables/NoItems";
import styles from "../../../../ui-lib/components/Tables/Table.module.css";
import PrimeTooltip from "../../../../ui-lib/components/PrimeTooltip/PrimeTooltip";

interface RowData {
  id: string;
  editing?: boolean;

  calendarId?: number;
  ruleType: number;
  order: number;
  alarmCode?: number | null;
  timeStart: string;
  timeEnd: string;
  primaryRespondentGroupId: number;
  secondaryRespondentGroupId?: number | null;
  secondaryRespondentGroupTimeout?: number | null;
  thirdRespondentGroupId?: number | null;
  thirdRespondentGroupTimeout?: number | null;
}

const isTimeoutInvalid = (value?: number | null, disabled?: boolean) => {
  if (disabled) {
    return false;
  }

  return value === null || value === undefined || value < 0 || value > 3600;
};

const ALARM_CODE_MIN = 1;
const ALARM_CODE_MAX = Number.MAX_SAFE_INTEGER;

const isAlarmCodeInvalid = (rowData: RowData) => {
  const disabled = rowData.ruleType === 2 || rowData.ruleType === 3;
  if (disabled) {
    return false;
  }
  return (
    rowData.alarmCode === null ||
    rowData.alarmCode === undefined ||
    rowData.alarmCode < ALARM_CODE_MIN ||
    rowData.alarmCode > ALARM_CODE_MAX
  );
};

const columns = (
  t: (key: string) => string,
  respondentGroups: RespondentGroupResponse[],
  onItemChange: (rowData: RowData) => void,
  hasTimeoutErrors: (rowData: RowData) => boolean,
  calendars?: Calendar[]
): TableColumn<RowData>[] => [
  {
    key: "type",
    style: { width: "240px" },
    header: t("AlarmReceptions:alarm_handling_table_column_rule_type"),
    fieldTemplate: (rowData) => {
      if (rowData.editing) {
        return (
          <TypeControl
            selected={rowData.ruleType}
            onSelect={(id) =>
              onItemChange({
                ...rowData,
                ruleType: id,
                timeStart: id === 1 ? "" : rowData.timeStart,
                timeEnd: id === 1 ? "" : rowData.timeEnd,
              })
            }
          />
        );
      }

      return (
        <TableCell
          value={t(
            `AlarmReceptions:alarm_handling_rule_type_${rowData.ruleType}`
          )}
        />
      );
    },
  },
  ...(calendars
    ? [
        {
          key: "calendar",
          style: { width: "240px" },
          header: t("AlarmReceptions:alarm_handling_rule_calendar"),
          fieldTemplate: (rowData: RowData) => {
            if (rowData.editing) {
              return (
                <RepeatControl
                  calendars={calendars}
                  selected={rowData.calendarId ?? 1}
                  onSelect={(id: number) =>
                    onItemChange({
                      ...rowData,
                      calendarId: id,
                    })
                  }
                />
              );
            }

            return (
              <TableCell
                value={t(
                  `AlarmReceptions:alarm_handling_rule_calendar_${
                    rowData.calendarId ?? 1
                  }`
                )}
              />
            );
          },
        },
      ]
    : []),
  {
    key: "code",
    style: { width: "100px" },
    editable: true,
    header: t("AlarmReceptions:alarm_handling_table_column_code"),
    fieldTemplate: (rowData) => {
      const disabled = rowData.ruleType === 2 || rowData.ruleType === 3;
      const hasError = isAlarmCodeInvalid(rowData);

      if (rowData.editing) {
        return (
          <NumberInput
            value={disabled ? "" : `${rowData.alarmCode}`}
            disabled={disabled}
            onChange={(e) => {
              const stringValue = e.currentTarget.value;
              const updatedValue =
                stringValue.length > 0
                  ? clamp(toNumber(stringValue), ALARM_CODE_MIN, ALARM_CODE_MAX)
                  : null;

              onItemChange({
                ...rowData,
                alarmCode: updatedValue,
              });
            }}
            withValidationMessage={false}
            validationError={hasError ? "Invalid alarm code" : undefined}
          />
        );
      }

      return <TableCell value={rowData.alarmCode ? rowData.alarmCode : ""} />;
    },
  },
  {
    key: "time",
    header: t("AlarmReceptions:alarm_handling_table_column_time"),
    style: { width: "320px" },
    fieldTemplate: (rowData) => {
      const disabled = rowData.ruleType === 1;

      if (rowData.editing) {
        return (
          <Row>
            <TimeControl
              disabled={disabled}
              value={disabled ? "" : rowData.timeStart}
              onChange={(time) =>
                onItemChange({
                  ...rowData,
                  timeStart: time ?? "",
                })
              }
            />
            <Spacer size={8} />
            <TimeControl
              disabled={disabled}
              value={disabled ? "" : rowData.timeEnd}
              onChange={(time) =>
                onItemChange({
                  ...rowData,
                  timeEnd: time ?? "",
                })
              }
            />
          </Row>
        );
      }

      const startString = encodeTime(rowData.timeStart);
      const endString = encodeTime(rowData.timeEnd);
      const timeString =
        startString && endString ? `${startString} - ${endString}` : "";

      return <TableCell value={timeString} />;
    },
  },
  {
    key: "respondent-group-1",
    style: { width: "240px" },
    header: t("AlarmReceptions:alarm_handling_table_column_respondent_group"),
    fieldTemplate: (rowData) => {
      if (rowData.editing) {
        return (
          <RespondentGroupControl
            respondentGroups={respondentGroups}
            selected={rowData.primaryRespondentGroupId}
            onSelect={(id) =>
              onItemChange({
                ...rowData,
                primaryRespondentGroupId: id as number,
              })
            }
          />
        );
      }

      const value = respondentGroups.find(
        (rg) => rg.id === rowData.primaryRespondentGroupId
      )?.description;

      return <TableCell value={value ?? ""} />;
    },
  },
  {
    key: "respondent-group-2",
    header: t(
      "AlarmReceptions:alarm_handling_table_column_respondent_group_secondary"
    ),
    style: { width: "240px" },
    fieldTemplate: (rowData) => {
      if (rowData.editing) {
        return (
          <RespondentGroupControl
            respondentGroups={respondentGroups}
            selected={rowData.secondaryRespondentGroupId}
            onSelect={(id) => {
              if (id === -1) {
                onItemChange({
                  ...rowData,
                  secondaryRespondentGroupId: null,
                  secondaryRespondentGroupTimeout: null,
                  thirdRespondentGroupId: null,
                  thirdRespondentGroupTimeout: null,
                });
                return;
              }
              onItemChange({
                ...rowData,
                secondaryRespondentGroupId: id,
              });
            }}
            withClearItem
          />
        );
      }

      const value = respondentGroups.find(
        (rg) => rg.id === rowData.secondaryRespondentGroupId
      )?.description;

      return <TableCell value={value ?? ""} />;
    },
  },
  {
    key: "respondent-group-2-timeout",
    header: t(
      "AlarmReceptions:alarm_handling_table_column_respondent_group_secondary_timeout"
    ),
    style: { width: "240px" },
    fieldTemplate: (rowData) => {
      const disabled = !rowData.secondaryRespondentGroupId;
      const hasError = isTimeoutInvalid(
        rowData.secondaryRespondentGroupTimeout,
        disabled
      );
      if (rowData.editing) {
        return (
          <TimeoutControl
            disabled={disabled}
            min={0}
            withValidationMessage={false}
            value={rowData.secondaryRespondentGroupTimeout}
            onChange={(value) =>
              onItemChange({
                ...rowData,
                secondaryRespondentGroupTimeout: value,
              })
            }
            hasError={hasError}
          />
        );
      }

      return (
        <TableCell
          value={(rowData.secondaryRespondentGroupTimeout ?? "").toString()}
        />
      );
    },
  },
  {
    key: "respondent-group-3",
    header: t(
      "AlarmReceptions:alarm_handling_table_column_respondent_group_third"
    ),
    style: { width: "240px" },
    fieldTemplate: (rowData) => {
      const disabled = !rowData.secondaryRespondentGroupTimeout?.toString();
      if (rowData.editing) {
        return (
          <RespondentGroupControl
            disabled={disabled}
            respondentGroups={respondentGroups}
            selected={rowData.thirdRespondentGroupId}
            onSelect={(id) => {
              if (id === -1) {
                onItemChange({
                  ...rowData,
                  thirdRespondentGroupId: null,
                  thirdRespondentGroupTimeout: null,
                });
                return;
              }
              onItemChange({
                ...rowData,
                thirdRespondentGroupId: id,
              });
            }}
            withClearItem
          />
        );
      }

      const value = respondentGroups.find(
        (rg) => rg.id === rowData.thirdRespondentGroupId
      )?.description;

      return <TableCell value={value ?? ""} />;
    },
  },
  {
    key: "respondent-group-3-timeout",
    header: t(
      "AlarmReceptions:alarm_handling_table_column_respondent_group_third_timeout"
    ),
    style: { width: "240px" },
    fieldTemplate: (rowData) => {
      const disabled =
        !rowData.thirdRespondentGroupId ||
        !rowData.secondaryRespondentGroupTimeout?.toString();
      const hasError = isTimeoutInvalid(
        rowData.thirdRespondentGroupTimeout,
        disabled
      );
      if (rowData.editing) {
        return (
          <TimeoutControl
            disabled={disabled}
            min={0}
            withValidationMessage={false}
            value={rowData.thirdRespondentGroupTimeout}
            onChange={(value) =>
              onItemChange({
                ...rowData,
                thirdRespondentGroupTimeout: value,
              })
            }
            hasError={hasError}
          />
        );
      }

      return (
        <TableCell
          value={(rowData.thirdRespondentGroupTimeout ?? "").toString()}
        />
      );
    },
  },
];

export const EditAlarmHandlingRules = ({
  respondentGroups,
  calendars,
  setModalFullScreen,
  isModalFullScreen,
}: {
  respondentGroups: RespondentGroupResponse[];
  calendars?: Calendar[];
  setModalFullScreen: (value: boolean) => void;
  isModalFullScreen: boolean;
}) => {
  const t = useTranslations();
  const { control, setValue } = useFormContext<AlarmHandlingDetailsParams>();
  const {
    fields: formValues,
    prepend,
    remove,
  } = useFieldArray({
    control,
    name: "rules",
  });

  const [editing, setEditing] = useState<string>();
  const [editedItems, setEditedItems] = useState<RowData[]>(formValues);

  // Creating a ref for get previous state of items
  const prevItemsRef = useRef<RowData[]>(editedItems);
  const editSnapshotRef = useRef<RowData[] | null>(null);

  useEffect(() => {
    // if edit in progress prevent setting items
    if (!editing) {
      setEditedItems(orderBy(formValues, "order"));
    }
  }, [formValues]);

  const items = useMemo(
    () =>
      orderBy(
        editedItems.map((item) => ({
          ...item,
          editing: item.id === editing,
        })),
        "Order"
      ),
    [editing, editedItems]
  );

  const addItem = () => {
    if (respondentGroups.length === 0) return;

    const newItem = {
      ...(calendars && { calendarId: calendars[0].id }),
      primaryRespondentGroupId: respondentGroups[0].id,
      ruleType: 1,
      timeEnd: "",
      timeStart: "",
      order: 1,
      Order: 1,
    };

    // Store snapshot before adding so cancel will remove the new row
    editSnapshotRef.current = editedItems.map((item) => ({ ...item }));
    prevItemsRef.current = editedItems;
    prepend(newItem);
  };

  const removeItem = (id: string) => {
    const index = formValues.findIndex((value) => value.id === id);
    remove(index);
  };

  const submitChanges = (data: RowData[]) => {
    const newFormValues = data.map((i) => ({
      ...omit(i, "id", "edited"),
    }));

    setValue("rules", newFormValues);
  };

  const updateItem = (updatedItem: RowData) => {
    setEditedItems((prev) => {
      const updatedItems = prev.map((item) =>
        item.id === updatedItem.id ? updatedItem : item
      );
      submitChanges(updatedItems);
      return updatedItems;
    });
  };

  const hasTimeoutErrors = (rowData: RowData) =>
    isTimeoutInvalid(
      rowData.secondaryRespondentGroupTimeout,
      !rowData.secondaryRespondentGroupId
    ) ||
    isTimeoutInvalid(
      rowData.thirdRespondentGroupTimeout,
      !rowData.thirdRespondentGroupId
    );

  const hasEditedRowErrors = useMemo(() => {
    const editedRow = editedItems.find((item) => item.id === editing);
    if (!editedRow) {
      return false;
    }

    return hasTimeoutErrors(editedRow) || isAlarmCodeInvalid(editedRow);
  }, [editedItems, editing]);

  const resetChanges = () => setEditedItems(formValues);

  const startEditing = (id: string) => {
    editSnapshotRef.current = editedItems.map((item) => ({ ...item }));
    setEditing(id);
  };

  const cancelEditing = () => {
    const snapshot = editSnapshotRef.current;

    if (snapshot) {
      setEditedItems(snapshot);
      submitChanges(snapshot);
    } else {
      resetChanges();
    }

    editSnapshotRef.current = null;
    setEditing(undefined);
  };

  const reorder = (list: RowData[]) => {
    const reorderedItems = list.map((i, index) => ({ ...i, Order: index + 1 }));

    submitChanges(reorderedItems);
  };

  useEffect(() => {
    // Set edited item only if it's new one
    if (prevItemsRef.current.length < editedItems.length) {
      setEditing(editedItems?.[0]?.id);
    }
  }, [editedItems.length]);

  return (
    <>
      <h2>{t("AlarmReceptions:alarm_handling_rules")}</h2>
      <Spacer size={16} />
      <Row type="fill">
        <div style={{ display: "flex", width: "80%" }}>
          <p className="grey-600">
            {t("AlarmReceptions:alarm_handling_rules_description")}
          </p>
        </div>
        <Row type="right" align="center">
          <Button
            size="small"
            variant="secondary"
            text={t("AlarmReceptions:add_new_rule")}
            onClick={addItem}
          />
          <Spacer size={16} />
          <PrimeTooltip target="#fullscreen" />
          <Button
            id="fullscreen"
            size="small"
            variant="secondary"
            image={isModalFullScreen ? "minimize" : "maximize"}
            onClick={() => setModalFullScreen(!isModalFullScreen)}
            dataPrTooltip={
              isModalFullScreen
                ? t("Common:exit_fullscreen")
                : t("Common:fullscreen")
            }
            dataPrMy="right bottom"
          />
        </Row>
      </Row>
      <Spacer size={32} />

      {items.length ? (
        <Table<RowData>
          items={items}
          columns={columns(
            t,
            respondentGroups,
            updateItem,
            hasTimeoutErrors,
            calendars
          )}
          withShowMore
          rowActionsFixed
          showRowsActionsOnHover
          reorderableRows
          onRowReorder={(result) => reorder(result.value)}
          showRowHover
          hideEmptyMessage
          rowActions={[
            {
              text: t("Common:label_edit"),
              icon: "pencil-alt",
              onClick: (rowData) => {
                startEditing(rowData.id);
              },
              hideForRow: (rowData) => editing === rowData.id,
            },
            {
              text: t("Common:delete"),
              icon: "trash",
              iconVariant: "secondary",
              onClick: (rowData) => {
                removeItem(rowData.id);
                setEditing(undefined);
              },
              hideForRow: (rowData) => editing === rowData.id,
            },
            {
              text: hasEditedRowErrors
                ? t("Errors:not_valid_values")
                : t("Common:save"),
              icon: "check",
              disabled: hasEditedRowErrors,
              style: hasEditedRowErrors ? { opacity: 0.5 } : undefined,
              onClick: () => {
                if (hasEditedRowErrors) return;

                submitChanges(editedItems);
                editSnapshotRef.current = null;
                setEditing(undefined);
              },
              hideForRow: (rowData) => editing !== rowData.id,
              alwaysShow: true,
            },
            {
              text: t("Common:cancel"),
              icon: "x",
              iconVariant: "secondary",
              onClick: cancelEditing,
              hideForRow: (rowData) => editing !== rowData.id,
              alwaysShow: true,
            },
          ]}
        />
      ) : (
        <NoItems
          title={t("Table:noresult_title")}
          subTitle={t("Table:noresult_subtitle")}
          className={styles.modalNoItems}
          icon="eye-off"
        />
      )}
    </>
  );
};
